import { Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { GtmService } from '@xbh/common-lib';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UrlConstants } from './pages/common/constants/url.constants';
import { openInNewTab } from './pages/common/utils/navigate-to';
import { DataCacheService } from './shared/data-save/data-cache.service';
import { EmulationHeaderComponent } from './shared/emulation-header/emulation-header.component';
import { NavMenuComponent, NavMenuItem } from './shared/nav-menu/nav-menu.component';
import { NavigationConfirmDialogComponent } from './shared/navigation-confirm-dialog/navigation-confirm-dialog.component';
import { PageFooterComponent } from './shared/page-footer/page-footer.component';
import { PageHeaderComponent } from './shared/page-header/page-header.component';
import { ScrollToTopButtonComponent } from './shared/scroll-to-top-button/scroll-to-top-button.component';
import { MasterDataStore } from './shared/services/master-data.store';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, PageHeaderComponent, PageFooterComponent, NavMenuComponent, EmulationHeaderComponent, ScrollToTopButtonComponent, NavigationConfirmDialogComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  protected readonly title = signal('mp');
  isMenuOpen = signal(false);
  /** ウィンドウを閉じる確認ダイアログ表示フラグ */
  showCloseConfirmDialog = signal(false);
  /** 確認ダイアログメッセージ */
  closeConfirmMessage = signal<string>('');
  dialogTitle = signal<string>('');
  dialogCategory = signal<string>('');
  okLabel = signal<string>('');
  cancelLabel = signal<string>('');
  showScrollToTopButton = signal(false);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly gtmService = inject(GtmService);
  /** マスタデータストア（アプリ起動時に一度だけロードする） */
  private readonly masterDataStore = inject(MasterDataStore);
  private readonly dataCacheService = inject(DataCacheService);

  @ViewChild('emulationHeader', { read: ElementRef })
  emulationHeaderRef?: ElementRef<HTMLElement>;

  private lastEmulationHeaderHeight = -1;

  ngOnInit() {
    const GTM_ID = 'GTM-K2WC2FV';
    // GTMコンテナIDを指定
    this.gtmService.init(GTM_ID);

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.gtmService.pushEvent({
        event: 'page_view',
        url: event.urlAfterRedirects,
      });

      const route = this.getDeepestActivatedRoute();
      this.showScrollToTopButton.set(route.snapshot.data['showScrollToTop'] === true);
    });

    const initialRoute = this.getDeepestActivatedRoute();
    this.showScrollToTopButton.set(initialRoute.snapshot.data['showScrollToTop'] === true);
  }

  private getDeepestActivatedRoute(): ActivatedRoute {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  ngAfterViewChecked(): void {
    const height = this.emulationHeaderRef?.nativeElement.offsetHeight ?? 0;
    if (height !== this.lastEmulationHeaderHeight) {
      this.lastEmulationHeaderHeight = height;
      document.documentElement.style.setProperty('--emulation-header-height', `${height}px`);
    }
  }

  get showEmulationHeader(): boolean {
    return this.dataCacheService.getEmulationSign() === '1';
  }

  menuItems: NavMenuItem[] = [];

  openMenu(): void {
    // メニュー項目を毎回最新の状態でセット
    this.menuItems = [
      {
        iconSrc: '/mp0010/ic-help-circle.svg',
        label: this.masterDataStore.getDisplayTitle(UrlConstants.FAQ_URL) ?? 'よくある質問',
        action: 'faq',
      },
      {
        iconSrc: '/nav-menu/close-window-icon.svg',
        label: 'ウィンドウを閉じる',
        action: 'close-window',
      },
    ];
    this.isMenuOpen.set(true);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  onMenuItemClick(action: string): void {
    if (action === 'faq') {
      // よくある質問ページを別タブで開く
      const faqUrl = this.masterDataStore.getTransitionDestinationUrl(UrlConstants.FAQ_URL);
      if (faqUrl) {
        openInNewTab(faqUrl);
      }
      this.isMenuOpen.set(false);
      return;
    }
    if (action === 'close-window') {
      this.onHeaderCloseWindowClick();
      this.isMenuOpen.set(false);
      return;
    }
    this.isMenuOpen.set(false);
  }

  /** PCヘッダーの「よくある質問」押下時の処理 */
  onHeaderFaqClick(): void {
    this.onMenuItemClick('faq');
  }

  /** PCヘッダーの「ウィンドウを閉じる」押下時の処理 */
  onHeaderCloseWindowClick(): void {
    if (this.tryClickMp0010CloseButton()) {
      return;
    }
    this.showWindowCloseConfirmDialog();
  }

  private tryClickMp0010CloseButton(): boolean {
    const mp0010CloseButton = document.querySelector<HTMLButtonElement>('.mp0010-close-button');
    if (!mp0010CloseButton) {
      return false;
    }
    mp0010CloseButton.click();
    return true;
  }

  private showWindowCloseConfirmDialog(): void {
    const msgInfo = this.masterDataStore.getMessageInfo('W2001');
    this.closeConfirmMessage.set(msgInfo?.messageMainText ?? '');
    this.dialogTitle.set(msgInfo?.modalTitle ?? '');
    this.dialogCategory.set(msgInfo?.messageCategory ?? '');
    this.okLabel.set(msgInfo?.modalButton1 ?? '');
    this.cancelLabel.set(msgInfo?.modalButton3 ?? '');
    this.showCloseConfirmDialog.set(true);
  }

  /**
   * ウィンドウを閉じる確認ダイアログで「はい」押下時の処理
   */
  onFooterCloseClick(): void {
    this.showWindowCloseConfirmDialog();
  }

  onCloseWindowConfirmed(): void {
    this.showCloseConfirmDialog.set(false);
    window.close();
  }

  /**
   * ウィンドウを閉じる確認ダイアログで「いいえ」押下時の処理
   */
  onCloseWindowCancelled(): void {
    this.showCloseConfirmDialog.set(false);
  }

  onCustomerUsageClick(): void {
    const mpAppIndex = window.location.pathname.indexOf('/mp-app');
    const deploymentBasePath = mpAppIndex > 0 ? window.location.pathname.substring(0, mpAppIndex) : '';
    const customerNumber = this.dataCacheService.getCustomerNumber();
    const routePath = `${deploymentBasePath}/mplog-app?customerNumber=${customerNumber}`;

    const targetUrl = `${environment.mplogBaseUrl}${routePath}`;
    // お客様利用内容画面への遷移処理
    window.location.href = targetUrl;
  }

  onEmulationLogout(): void {
    window.close(); // 閉じる処理
  }
}
